/**
 * Local-browser draft persistence for long admin forms (property builder,
 * checklist builder). Keeps an in-progress form in localStorage so an accidental
 * back/refresh/close — or a failed save — doesn't lose everything; the next time
 * the form opens we offer to restore it.
 *
 * Scope + limits (by design, for now):
 *   - CREATE flows only — we don't draft edits of already-saved records.
 *   - Serializable fields only. Picked image File objects are NOT stored (binary,
 *     and they blow past the ~5MB localStorage quota), so a restored draft keeps
 *     all text/checklist/location and the user re-attaches photos. (When property
 *     photos move to upload-on-pick, the resulting URL can be drafted too.)
 *   - Per-browser, not per-user/-device. Keys are app-namespaced; cleared on a
 *     successful save or an explicit "start fresh".
 */
import { useCallback, useEffect, useRef } from "react";

const PREFIX = "ndhome:draft:";
const keyOf = (key) => PREFIX + key;

// Read a saved draft → { data, savedAt } or null. Never throws (private mode /
// corrupt JSON / quota all degrade to "no draft").
export const readDraft = (key) => {
  try {
    const raw = localStorage.getItem(keyOf(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || !parsed.savedAt) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const writeDraft = (key, data) => {
  try {
    localStorage.setItem(keyOf(key), JSON.stringify({ data, savedAt: Date.now() }));
  } catch {
    // Best-effort: a full/blocked store just means no draft this time.
  }
};

export const clearDraft = (key) => {
  try {
    localStorage.removeItem(keyOf(key));
  } catch {
    /* ignore */
  }
};

// "3:42 PM" / "Jun 20, 3:42 PM" — a friendly stamp for the restore prompt.
export const draftSavedLabel = (savedAt) => {
  if (!savedAt) return "";
  const d = new Date(savedAt);
  const sameDay = new Date().toDateString() === d.toDateString();
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return sameDay
    ? time
    : `${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}, ${time}`;
};

/**
 * Debounced autosave of a form snapshot. `buildSnapshot`/`isMeaningful` are read
 * through a ref so the returned `saveSoon`/`saveNow` keep stable identities (safe
 * to use as effect deps / event handlers). Nothing is written unless `enabled`
 * and the snapshot is "meaningful" (so an untouched form never leaves a phantom
 * draft behind).
 */
export const useDraftSaver = (
  key,
  { enabled, buildSnapshot, isMeaningful, delay = 600 },
) => {
  const ref = useRef({});
  ref.current = { enabled, buildSnapshot, isMeaningful };
  const timer = useRef(null);

  const saveNow = useCallback(() => {
    const cfg = ref.current;
    if (!cfg.enabled) return;
    const snap = cfg.buildSnapshot?.();
    if (!snap) return;
    if (cfg.isMeaningful && !cfg.isMeaningful(snap)) return;
    writeDraft(key, snap);
  }, [key]);

  const saveSoon = useCallback(() => {
    if (!ref.current.enabled) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(saveNow, delay);
  }, [saveNow, delay]);

  useEffect(() => () => timer.current && clearTimeout(timer.current), []);

  return { saveSoon, saveNow };
};

// Make a checklist array safe to persist: drop non-serializable picked File
// objects, and null out any `blob:` preview URL (object URLs are per-session, so
// a restored draft can't use them — only real uploaded URLs survive). Doesn't
// mutate the live state.
export const stripItemFiles = (items = []) =>
  (items || []).map(({ imageFile, ...rest }) => ({
    ...rest,
    image:
      typeof rest.image === "string" && rest.image.startsWith("blob:")
        ? null
        : rest.image,
  }));

/**
 * Native "Leave site? Changes you made may not be saved" prompt on tab
 * refresh/close. `shouldWarn` is a predicate evaluated at unload time (read via
 * a ref, so it always sees current state) — return true only while there's
 * unsaved work, so we never nag on a clean page. One listener for the
 * component's life; no per-change add/remove churn.
 */
export const useBeforeUnloadWarning = (shouldWarn) => {
  const ref = useRef(shouldWarn);
  ref.current = shouldWarn;
  useEffect(() => {
    const handler = (e) => {
      let warn = false;
      try {
        warn = !!ref.current?.();
      } catch {
        warn = false;
      }
      if (!warn) return undefined;
      // Modern browsers show their own generic copy; both lines are required
      // across browsers to actually trigger the prompt.
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);
};
